import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { rosterApi } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Calendar, Clock, User, ChevronRight } from 'lucide-react';

/**
 * "My upcoming shifts" — small widget for supervisor/organisation-admin users
 * showing shifts assigned to THEM personally over the next 7 days.
 *
 * Props:
 *   - userId: string — the current user's id
 */
export function MyUpcomingShiftsPanel({ userId }) {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    const now = new Date();
    const in7 = new Date(now); in7.setDate(in7.getDate() + 7);
    const toLocalDate = (d) => {
      const pad = (x) => String(x).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };
    rosterApi.listShifts({
      worker_id: userId,
      date_from: toLocalDate(now),
      date_to: toLocalDate(in7),
    })
      .then((res) => {
        // Only future/current shifts, sorted soonest first
        const upcoming = (res.data || [])
          .filter(s => new Date(s.end_at || s.start_at) >= new Date())
          .sort((a, b) => a.start_at.localeCompare(b.start_at));
        setShifts(upcoming);
      })
      .catch(() => setShifts([]))
      .finally(() => setLoading(false));
  }, [userId]);

  if (loading) return null;

  return (
    <Card className="bg-slate-800 border-slate-700" data-testid="my-upcoming-shifts-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-white flex items-center gap-2">
          <Calendar className="h-5 w-5 text-indigo-400" />
          My upcoming shifts
          <Badge variant="outline" className="ml-1 border-slate-600 text-slate-300" data-testid="my-shifts-count">
            {shifts.length} next 7 days
          </Badge>
          <Link
            to="/roster"
            className="ml-auto text-xs text-teal-400 hover:text-teal-300 inline-flex items-center"
            data-testid="my-shifts-open-roster"
          >
            Open roster <ChevronRight className="h-3 w-3" />
          </Link>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {shifts.length === 0 ? (
          <div className="py-6 text-center text-slate-500 text-sm" data-testid="my-shifts-empty">
            You have no shifts booked in the next 7 days.
          </div>
        ) : (
          <ul className="divide-y divide-slate-700">
            {shifts.map((s) => {
              const start = new Date(s.start_at);
              const end = new Date(s.end_at);
              const dateLabel = start.toLocaleDateString('en-AU', {
                timeZone: 'Australia/Sydney', weekday: 'short', day: '2-digit', month: 'short',
              });
              const timeLabel = `${start.toLocaleTimeString('en-AU', { timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit' })} → ${end.toLocaleTimeString('en-AU', { timeZone: 'Australia/Sydney', hour: '2-digit', minute: '2-digit' })}`;
              return (
                <li key={s.id} className="py-2.5 flex items-center gap-3" data-testid={`my-shift-${s.id}`}>
                  <div className="text-center min-w-[70px]">
                    <div className="text-xs text-slate-400 uppercase">{dateLabel.split(',')[0]}</div>
                    <div className="text-white font-semibold text-sm">{dateLabel.split(',')[1]?.trim()}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-200 text-sm inline-flex items-center gap-1.5">
                      <Clock className="h-3 w-3 text-slate-400" />
                      {timeLabel}
                    </div>
                    <div className="text-xs text-slate-400 inline-flex items-center gap-1.5 mt-0.5">
                      <User className="h-3 w-3" />
                      {s.client_names?.length ? s.client_names.join(', ') : (s.client_name || <span className="italic">no client assigned</span>)}
                      {s.has_conflict && <Badge variant="outline" className="ml-2 text-[10px] border-red-500/50 text-red-300">⚠ conflict</Badge>}
                    </div>
                    {s.co_workers?.length > 0 && (
                      <div className="text-xs text-teal-300 mt-0.5" data-testid={`upcoming-co-workers-${s.id}`}>
                        Team {s.group_size}:{s.client_ids?.length || 1} · with {s.co_workers.map(c => c.name || 'open slot').join(', ')}
                      </div>
                    )}
                  </div>
                  <Badge variant="outline" className="text-[10px] border-slate-600 text-slate-300 uppercase">{s.shift_type?.replace(/_/g, ' ')}</Badge>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
